import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Palette, Check, RotateCcw, Upload, Image, Building2, Loader2, Trash2 } from "lucide-react";

// Default brand colors
const DEFAULT_PRIMARY = "#3B82F6"; // Blue
const DEFAULT_SECONDARY = "#10B981"; // Emerald

interface ColorPickerProps {
  label: string;
  description: string;
  value: string;
  onChange: (color: string) => void;
}

function ColorPicker({ label, description, value, onChange }: ColorPickerProps) {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Validate hex color format
    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-medium">{label}</Label>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>

      <div className="flex items-center gap-3">
        {/* Color Preview Swatch */}
        <div
          className="relative h-12 w-12 cursor-pointer overflow-hidden rounded-lg border-2 border-gray-200 shadow-sm"
          style={{ backgroundColor: value }}
        >
          <input
            type="color"
            value={value}
            onChange={handleColorPickerChange}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            title="Choose color"
          />
        </div>

        {/* Hex Input */}
        <div className="flex-1">
          <Input
            value={inputValue}
            onChange={handleInputChange}
            placeholder="#000000"
            className="font-mono text-sm uppercase"
            maxLength={7}
          />
        </div>
      </div>
    </div>
  );
}

interface LogoUploadProps {
  title: string;
  description: string;
  logoUrl: string | null;
  isUploading: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
  backgroundColor: "light" | "dark";
}

function LogoUpload({
  title,
  description,
  logoUrl,
  isUploading,
  onUpload,
  onRemove,
  backgroundColor,
}: LogoUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await onUpload(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-start gap-6">
      {/* Logo Preview */}
      <div className="relative">
        <div
          className={`flex h-24 w-40 items-center justify-center rounded-lg border-2 ${
            backgroundColor === "dark" ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
          }`}
        >
          {logoUrl ? (
            <img src={logoUrl} alt={title} className="max-h-20 max-w-36 object-contain" />
          ) : (
            <Building2
              className={`h-10 w-10 ${backgroundColor === "dark" ? "text-white" : "text-gray-400"}`}
            />
          )}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50">
              <Loader2 className="h-6 w-6 animate-spin text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Upload Controls */}
      <div className="flex-1 space-y-2">
        <div>
          <Label className="text-sm font-medium">{title}</Label>
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            {logoUrl ? "Change" : "Upload"}
          </Button>
          {logoUrl && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              disabled={isUploading}
              className="gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function BrandingTab() {
  const { toast } = useToast();

  // Queries and mutations
  const brandColors = useQuery(api.organizations.getBrandColors);
  const organization = useQuery(api.organizations.getByUser);
  const updateBrandColors = useMutation(api.organizations.updateBrandColors);
  const generateLogoUploadUrl = useMutation(api.organizations.generateLogoUploadUrl);
  const updateProfile = useMutation(api.organizations.updateProfile);

  // State
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY);
  const [hasColorChanges, setHasColorChanges] = useState(false);
  const [isSavingColors, setIsSavingColors] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingLogoDark, setIsUploadingLogoDark] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoDarkUrl, setLogoDarkUrl] = useState<string | null>(null);

  // Load saved colors when data is available
  useEffect(() => {
    if (brandColors) {
      setPrimaryColor(brandColors.primaryColor || DEFAULT_PRIMARY);
      setSecondaryColor(brandColors.secondaryColor || DEFAULT_SECONDARY);
    }
  }, [brandColors]);

  // Load logos from organization
  useEffect(() => {
    if (organization?.logo) {
      setLogoUrl(organization.logo);
    }
    if (organization?.logoDark) {
      setLogoDarkUrl(organization.logoDark);
    }
  }, [organization]);

  // Track color changes
  useEffect(() => {
    if (brandColors) {
      const savedPrimary = brandColors.primaryColor || DEFAULT_PRIMARY;
      const savedSecondary = brandColors.secondaryColor || DEFAULT_SECONDARY;
      setHasColorChanges(primaryColor !== savedPrimary || secondaryColor !== savedSecondary);
    }
  }, [primaryColor, secondaryColor, brandColors]);

  const handleSaveColors = async () => {
    setIsSavingColors(true);
    try {
      await updateBrandColors({
        primaryColor,
        secondaryColor,
      });

      toast({
        title: "Brand colors saved",
        description: "Your brand colors have been updated successfully.",
      });
      setHasColorChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save brand colors",
        variant: "destructive",
      });
    } finally {
      setIsSavingColors(false);
    }
  };

  const handleResetColors = () => {
    if (brandColors) {
      setPrimaryColor(brandColors.primaryColor || DEFAULT_PRIMARY);
      setSecondaryColor(brandColors.secondaryColor || DEFAULT_SECONDARY);
    } else {
      setPrimaryColor(DEFAULT_PRIMARY);
      setSecondaryColor(DEFAULT_SECONDARY);
    }
  };

  const uploadLogo = async (file: File, type: "logo" | "logoDark") => {
    const setUploading = type === "logo" ? setIsUploadingLogo : setIsUploadingLogoDark;
    const setUrl = type === "logo" ? setLogoUrl : setLogoDarkUrl;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Error",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image must be smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      // Step 1: Get upload URL from Convex storage
      const uploadUrl = await generateLogoUploadUrl();

      // Step 2: Upload file to Convex storage
      const uploadResponse = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const { storageId } = await uploadResponse.json();

      // Step 3: Update organization with the logo storage ID
      if (type === "logo") {
        await updateProfile({ logo: storageId });
      } else {
        await updateProfile({ logoDark: storageId });
      }

      // Update local state
      setUrl(URL.createObjectURL(file));

      toast({
        title: "Success!",
        description: `${type === "logo" ? "Logo" : "Dark background logo"} uploaded successfully!`,
      });
    } catch (error) {
      console.error("Logo upload error:", error);
      toast({
        title: "Error",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async (type: "logo" | "logoDark") => {
    const setUrl = type === "logo" ? setLogoUrl : setLogoDarkUrl;

    try {
      if (type === "logo") {
        await updateProfile({ logo: undefined });
      } else {
        await updateProfile({ logoDark: undefined });
      }
      setUrl(null);
      toast({
        title: "Logo removed",
        description: `${type === "logo" ? "Logo" : "Dark background logo"} has been removed.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove logo",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Logo Section */}
      <Card className="p-6">
        <div className="mb-6 flex items-center gap-3">
          <Image className="h-6 w-6 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold">Company Logos</h2>
            <p className="text-sm text-gray-500">Upload logos for use in documents and proposals</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Light Background Logo (Default) */}
          <LogoUpload
            title="Logo (Light Background)"
            description="Primary logo for light/white backgrounds. This is your default logo."
            logoUrl={logoUrl}
            isUploading={isUploadingLogo}
            onUpload={(file) => uploadLogo(file, "logo")}
            onRemove={() => removeLogo("logo")}
            backgroundColor="light"
          />

          <div className="border-t pt-6">
            {/* Dark Background Logo */}
            <LogoUpload
              title="Logo (Dark Background)"
              description="Alternate logo for dark/colored backgrounds. Used when document covers have dark themes."
              logoUrl={logoDarkUrl}
              isUploading={isUploadingLogoDark}
              onUpload={(file) => uploadLogo(file, "logoDark")}
              onRemove={() => removeLogo("logoDark")}
              backgroundColor="dark"
            />
          </div>
        </div>

        <p className="mt-6 text-xs text-gray-500">
          Recommended: PNG or SVG format with transparent background. Any dimensions supported.
        </p>
      </Card>

      {/* Brand Colors Section */}
      <Card className="p-6">
        <div className="mb-6 flex items-center gap-3">
          <Palette className="h-6 w-6 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold">Brand Colors</h2>
            <p className="text-sm text-gray-500">
              Customize your organization's brand colors for documents and proposals
            </p>
          </div>
        </div>

        {/* Color Pickers */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <ColorPicker
            label="Primary Color"
            description="Main brand color used for headers and key elements"
            value={primaryColor}
            onChange={setPrimaryColor}
          />

          <ColorPicker
            label="Secondary Color"
            description="Accent color for highlights and secondary elements"
            value={secondaryColor}
            onChange={setSecondaryColor}
          />
        </div>

        {/* Color Preview */}
        <div className="mt-6 border-t pt-6">
          <Label className="mb-3 block text-sm font-medium">Preview</Label>
          <div className="flex items-center gap-4">
            <div className="flex-1 rounded-lg bg-gray-50 p-4">
              <div className="mb-2 h-3 rounded-full" style={{ backgroundColor: primaryColor }} />
              <div className="h-2 w-3/4 rounded-full" style={{ backgroundColor: secondaryColor }} />
              <div className="mt-4 flex gap-2">
                <Button
                  size="sm"
                  style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                  className="text-white hover:opacity-90"
                >
                  Primary Button
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  style={{ borderColor: secondaryColor, color: secondaryColor }}
                  className="hover:opacity-90"
                >
                  Secondary Button
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-2 border-t pt-6">
          <Button
            onClick={handleSaveColors}
            disabled={!hasColorChanges || isSavingColors}
            className="gap-2"
          >
            <Check className="h-4 w-4" />
            {isSavingColors ? "Saving..." : "Save Colors"}
          </Button>
          <Button
            onClick={handleResetColors}
            variant="outline"
            disabled={!hasColorChanges}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      </Card>

      {/* Usage Info */}
      <Card className="border-blue-100 bg-blue-50 p-4">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> Your logos and brand colors will be used in your generated
          documents, proposals, and service agreements. The document writer will automatically
          select the appropriate logo based on the background color.
        </p>
      </Card>
    </div>
  );
}
